/**
 * Report filter chips and the Report Settings badge — one implementation, driven by
 * the same section presets as the drawer (components/v2/reportFilterSections.js).
 *
 * Replaces eight page-local copies (docs/DUPLICATION_AUDIT.md, B16 / A22):
 *   - Three resolved names by taking the FIRST id field an option happened to have
 *     (area_id || … || device_category_id || device_id …), so a resident row that also
 *     carries area_id, or a device that carries device_category_id, matched the wrong
 *     record, and reasons were looked up by reason_code_id although they are selected
 *     by cloud_reason_code_id. Each sub-section now matches on its own idKey.
 *   - Three showed only counts ("Areas: 3"); every page now shows names.
 *   - The badge counted selected ITEMS on four pages and active FILTERS on four, so
 *     picking 3 areas read "3" on one report and "1" on the next. It now counts active
 *     filters — one per chip, plus a time-of-day window that is not all day.
 */

import { describeTimeWindow } from './reportTimeWindow.js';
import { ALERT_REPORT_SECTIONS } from '../components/v2/reportFilterSections.js';

const OPERATOR_SYMBOL = { lt: '<', gt: '>', eq: '=' };
const MAX_NAMES = 3;

/**
 * @param {object} filters   — the page's reportFilters ({ key: id[] , responseTime, timeWindow })
 * @param {object} options   — the page's filterOptions (option arrays by optionsKey)
 * @param {Array}  [sections] — the drawer's section preset (default: alert groups)
 * @param {{ responseTimeScope?: string }} [opts] — appended to the Response Time chip,
 *        e.g. Executive's "reason chart only" when the filter does not reach every section
 * @returns {Array<{ key: string, label: string }>}
 */
export function buildFilterChips(filters, options, sections, { responseTimeScope } = {}) {
  const chips = [];
  const f = filters || {};
  (sections || ALERT_REPORT_SECTIONS).forEach((section) => {
    section.subs.forEach((sub) => {
      const ids = f[sub.key];
      if (!Array.isArray(ids) || ids.length === 0) return;
      const opts = options?.[sub.optionsKey] || [];
      const names = ids.slice(0, MAX_NAMES).map((id) => {
        const opt = opts.find((o) => String(o[sub.idKey]) === String(id));
        return opt && opt[sub.nameKey] ? opt[sub.nameKey] : String(id);
      });
      const more = ids.length > MAX_NAMES ? ` +${ids.length - MAX_NAMES} more` : '';
      chips.push({ key: sub.key, label: `${sub.chipLabel || sub.label}: ${names.join(', ')}${more}` });
    });
  });
  if (f.responseTime?.enabled) {
    const rt = f.responseTime;
    chips.push({
      key: 'responseTime',
      label: `Response Time: ${OPERATOR_SYMBOL[rt.operator] || '<'} ${rt.value} min${responseTimeScope ? ` (${responseTimeScope})` : ''}`,
    });
  }
  return chips;
}

/**
 * The Report Settings badge: active filters, not selected items. One per sub-section
 * with a selection, one for an enabled Response Time, one for a time-of-day window
 * that is not all day (the window is shown on the range chip, not as a filter chip).
 */
export function countActiveFilters(filters, sections) {
  const f = filters || {};
  let n = 0;
  (sections || ALERT_REPORT_SECTIONS).forEach((section) => section.subs.forEach((sub) => {
    if (Array.isArray(f[sub.key]) && f[sub.key].length > 0) n += 1;
  }));
  if (f.responseTime?.enabled) n += 1;
  if (describeTimeWindow(f.timeWindow?.start, f.timeWindow?.end)) n += 1;
  return n;
}
